import { useState } from 'react';
import { Box, Card, CardContent, CardHeader, Typography } from '@mui/material';
import { ApolloError } from '@apollo/client';

import { LoadedContent } from '@occc/ui-design-system';
import { KioskFragment } from '@occc/graphql';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';

import { TargetedKiosksList } from './targeted-kiosks-list.component';

import { useGetKiosks } from '../../hooks/use-get-kiosks.hook';
import {
  TARGETED_KIOSKS_SECTION_TITLE,
  KIOSK_ID_COLUMN_LABEL,
  KIOSK_ADDRESS_COLUMN_LABEL,
} from '../../touchpoint.constants';

type TargetedKiosksSectionProps = {
  defaultScreenId?: string;
};

export const TargetedKiosksSection = ({ defaultScreenId }: TargetedKiosksSectionProps) => {
  const [pageSize] = useState(10);
  const [paginationInfo, onPageChange] = usePaginationParams({
    pageSize,
    afterParamName: 'targetedKiosksAfter',
    beforeParamName: 'targetedKiosksBefore',
  });

  const {
    data: kiosks,
    loading,
    error,
    paginationProps,
  } = useGetKiosks({
    filter: {
      defaultScreenUuidIncludes: defaultScreenId ? [defaultScreenId] : undefined,
    },
    pageSize,
    paginationInfo,
    onPageChange,
    baseOptions: {
      skip: !defaultScreenId,
    },
  });

  const renderData = (items: KioskFragment[]) => {
    if (!items || items.length === 0) {
      return <Box />;
    }

    return (
      <>
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <Typography variant='body2' fontWeight={400} sx={{ flex: 1 }}>
            {KIOSK_ID_COLUMN_LABEL}
          </Typography>
          <Typography variant='body2' fontWeight={400} sx={{ flex: 1 }}>
            {KIOSK_ADDRESS_COLUMN_LABEL}
          </Typography>
        </Box>
        <TargetedKiosksList kiosks={items} paginationProps={paginationProps} />
      </>
    );
  };

  return (
    <Box mb={2}>
      <Card>
        <CardHeader
          title={
            <Typography variant='subtitle1' paragraph>
              {TARGETED_KIOSKS_SECTION_TITLE}
            </Typography>
          }
        />
        <CardContent>
          <LoadedContent<KioskFragment>
            error={error as ApolloError | undefined}
            loading={loading}
            items={kiosks}
            renderData={renderData}
          />
        </CardContent>
      </Card>
    </Box>
  );
};
